import { AmountBreakdowns, BenefitItems, getCeilingAmountLabel, CoveragePlan } from 'all-in-one-shared-api';
import { Collapse } from 'antd';
import {
  getAccommodationLevelDescription,
  getBreakdownLabel,
  getLimitLabel,
  getMaxAmountLabel,
} from 'pages/benefits/utils/utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getDeductibleValue } from '../coverageConditions';
import AccordionTitle from './accordionTitle/AccordionTitle';
import { BenefitDisplayContent } from './BenefitDisplayContent';
import CoverageContent from './CoverageContent';
import { limittimes } from '../../../../configs/getConfig';

interface BenefitAccordionViewProps {
  coveragePlan: CoveragePlan;
  benefitItems: Array<BenefitItems>;
  showItemAccomodation: boolean;
  showItemReimbursementRate: boolean;
}

const Panel = styled(Collapse.Panel)`
  .ant-collapse-content-box {
    padding: 0;
  }
`;

const BenefitAccordionView = ({
  coveragePlan,
  benefitItems,
  showItemAccomodation,
  showItemReimbursementRate,
}: BenefitAccordionViewProps): JSX.Element => {
  const { t, i18n } = useTranslation();

  const getAmountBreakdownData = (
    amountBreakdowns: Array<AmountBreakdowns> | undefined,
    maxAmountDescription: string | undefined,
  ) => {
    if (!amountBreakdowns) return [];

    const breakdownList: BenefitDisplayContent[] = [];

    const complexAmountBreakdowns = amountBreakdowns.filter((b) => b.name == 'Complex');
    if (complexAmountBreakdowns.length) {
      const complexAmount = complexAmountBreakdowns[0];
      const name = getBreakdownLabel(i18n.language, complexAmount);
      if (complexAmount.amount) {
        breakdownList.push({
          title: name ?? '',
          value: `HKD ${parseInt(complexAmount.amount).toLocaleString()}`,
        });
      }
    }
    const majorAmountBreakdowns = amountBreakdowns.filter((b) => b.name == 'Major');
    if (majorAmountBreakdowns.length) {
      const majorAmount = majorAmountBreakdowns[0];
      const name = getBreakdownLabel(i18n.language, majorAmount);
      if (majorAmount.amount) {
        breakdownList.push({
          title: name ?? '',
          value: `HKD ${parseInt(majorAmount.amount).toLocaleString()}`,
        });
      }
    }

    const intermediateAmountBreakdowns = amountBreakdowns.filter((b) => b.name == 'Intermediate');
    if (intermediateAmountBreakdowns.length) {
      const intermediateAmount = intermediateAmountBreakdowns[0];
      const name = getBreakdownLabel(i18n.language, intermediateAmount);
      if (intermediateAmount.amount) {
        breakdownList.push({
          title: name ?? '',
          value: `HKD ${parseInt(intermediateAmount.amount).toLocaleString()}`,
        });
      }
    }

    const minorAmountBreakdowns = amountBreakdowns.filter((b) => b.name == 'Minor');
    if (minorAmountBreakdowns.length) {
      const minorAmount = minorAmountBreakdowns[0];
      const name = getBreakdownLabel(i18n.language, minorAmount);
      if (minorAmount.amount) {
        breakdownList.push({
          title: name ?? '',
          value: `HKD ${parseInt(minorAmount.amount).toLocaleString()}`,
        });
      }
    }

    // Note(Andy Chow): please refer to https://generali-hk.atlassian.net/browse/AIOBRB-1688
    if (breakdownList.length > 0 && maxAmountDescription) {
      breakdownList.unshift({
        value: maxAmountDescription,
      });
    }

    // Note(Andy Chow): Need to show maxLimit/maxAmount if there is an all-in-one value according to the test case 6 in https://generali-hk.atlassian.net/browse/AIOBRB-1619
    if (breakdownList.length === 0 && amountBreakdowns.length === 1) {
      if (amountBreakdowns[0]?.amount) {
        breakdownList.push({
          title: maxAmountDescription,
          value: `HKD ${parseInt(amountBreakdowns[0].amount).toLocaleString()}`,
        });
      }
    }

    return breakdownList;
  };

  const getCoverageData = (coveragePlan: CoveragePlan, benefit: BenefitItems): BenefitDisplayContent[] => {
    const items: BenefitDisplayContent[] = [];
    const deductible = parseInt(benefit.deductibleAmount ?? '0');
    const deductibleValue = getDeductibleValue(coveragePlan, deductible);

    if (deductibleValue) {
      items.push({
        title: t('benefits.deductible'),
        value: deductibleValue,
      });
    }

    const { maxAmount, isNetwork } = benefit;
    if (maxAmount) {
      const label = getCeilingAmountLabel({
        plan: coveragePlan,
        amount: Number(maxAmount),
        unlimitedLabel: t('benefits.unlimited'),
        asChargedLabel: t('benefits.asCharged'),
        panelServiceLabel: t('benefits.panelService'),
        isNetwork,
      });
      items.push({
        title: getMaxAmountLabel(i18n.language, benefit) ?? '',
        value: label,
      });
    }

    const { limit } = benefit;
    if (limit) {
      const label = limit == limittimes ? t('benefits.unlimited') : limit.toString();
      items.push({
        title: getLimitLabel(i18n.language, benefit) ?? '',
        value: label,
      });
    }

    if (showItemAccomodation) {
      const { accommodationLevel } = benefit;
      if (accommodationLevel) {
        items.push({
          title: t('benefits.accommodationLevel'),
          value: getAccommodationLevelDescription(i18n.language, benefit) ?? '',
        });
      }
    }

    return items;
  };

  const getContentData = (benefit: BenefitItems): BenefitDisplayContent[] => {
    const benefitContent = [];

    if ((benefit.amountBreakdowns?.length ?? 0) > 0) {
      const maxAmountLabel = getMaxAmountLabel(i18n.language, benefit) ?? '';
      const amountBreakdownData = getAmountBreakdownData(benefit.amountBreakdowns, maxAmountLabel);
      if (amountBreakdownData.length > 0) {
        benefitContent.push(...amountBreakdownData);
      }
    } else {
      benefitContent.push(...getCoverageData(coveragePlan, benefit));
    }

    if (showItemReimbursementRate) {
      const { reimbursementRate } = benefit;
      if (reimbursementRate) {
        benefitContent.push({
          title: t('benefits.reimbursementRate'),
          value: `${Math.round(parseFloat(reimbursementRate))}%`,
        });
      }
    }

    return benefitContent;
  };

  return (
    <Collapse>
      {benefitItems.map((benefit) => (
        <Panel key={`${benefit?.id}`} header={<AccordionTitle benefit={benefit} />}>
          <CoverageContent items={getContentData(benefit)} />
        </Panel>
      ))}
    </Collapse>
  );
};

export default BenefitAccordionView;
